import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Package, ShoppingBag, Mail, Settings, LogOut, Home, FileText, Sparkles, Menu, X, RefreshCw, Truck, BarChart3, Gift, AlertTriangle } from "lucide-react";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/campaigns", label: "Campaigns", icon: Gift },
  { to: "/admin/custom-orders", label: "Commissions", icon: Mail },
  { to: "/admin/remakes", label: "Remake Requests", icon: RefreshCw },
  { to: "/admin/workshops", label: "Workshops", icon: Sparkles },
  { to: "/admin/shipping", label: "Shipping", icon: Truck },
  { to: "/admin/content", label: "Site Content", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function SidebarLinks({ onNavigate }) {
  return (
    <nav className="flex-1 p-4 space-y-1">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
              isActive ? "bg-[#161616] text-[#B87333] border-l-2 border-[#B87333]" : "text-[#A3A3A3] hover:text-[#F5F5DC]"
            }`
          }
        >
          <Icon size={16} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function SideContent({ onNav, onClose, onLogout, onViewSite }) {
  return (
    <>
      <div className="p-6 border-b border-[#2A2A2A] flex items-center justify-between">
        <div>
          <p className="font-display text-2xl text-[#F5F5DC]">David&apos;s</p>
          <p className="smallcaps mt-1">Workshop</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-[#A3A3A3]" aria-label="Close menu">
            <X size={22} />
          </button>
        )}
      </div>
      <SidebarLinks onNavigate={onNav} />
      <div className="p-4 border-t border-[#2A2A2A] space-y-2">
        <button onClick={onViewSite} className="flex items-center gap-3 text-sm text-[#A3A3A3] hover:text-[#F5F5DC] w-full px-4 py-2" data-testid="admin-view-site">
          <Home size={16} /> View Site
        </button>
        <button onClick={onLogout} className="flex items-center gap-3 text-sm text-[#A3A3A3] hover:text-[#CF6679] w-full px-4 py-2" data-testid="admin-logout">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Poll maintenance status on route change so the warning banner reflects current state.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/maintenance")
      .then((r) => { if (!cancelled) setMaintenance(!!r.data?.maintenance_mode); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Hide the platform-injected "Made with Emergent" badge on all admin routes so
  // it can't overlap the sticky Save button on mobile. The platform sets inline
  // styles + z-index:9999, so we need to (a) inject a stylesheet with !important
  // AND (b) force `display:none` on the element itself with priority, then keep
  // re-applying via a MutationObserver in case the platform re-renders it.
  useEffect(() => {
    const STYLE_ID = "hide-emergent-badge-admin";
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = "#emergent-badge{display:none !important;visibility:hidden !important;pointer-events:none !important;}";
      document.head.appendChild(style);
    }
    const hide = () => {
      const b = document.getElementById("emergent-badge");
      if (b) {
        b.style.setProperty("display", "none", "important");
        b.style.setProperty("visibility", "hidden", "important");
        b.style.setProperty("pointer-events", "none", "important");
      }
    };
    hide();
    // Watch for the badge being re-inserted/mutated by the platform script.
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    return () => {
      observer.disconnect();
      const s = document.getElementById(STYLE_ID);
      if (s) s.remove();
      const b = document.getElementById("emergent-badge");
      if (b) {
        b.style.removeProperty("display");
        b.style.removeProperty("visibility");
        b.style.removeProperty("pointer-events");
      }
    };
  }, []);

  const currentLabel = items.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label || "Admin";

  const handleLogout = () => { logout(); nav("/admin/login"); };
  const handleViewSite = () => nav("/");

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-[#2A2A2A] bg-[#0A0A0A] flex-col" data-testid="admin-sidebar">
        <SideContent onLogout={handleLogout} onViewSite={handleViewSite} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/70 z-40" onClick={() => setDrawerOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-[#0A0A0A] border-r border-[#2A2A2A] flex flex-col z-50" data-testid="admin-drawer">
            <SideContent
              onNav={() => setDrawerOpen(false)}
              onClose={() => setDrawerOpen(false)}
              onLogout={handleLogout}
              onViewSite={handleViewSite}
            />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-lg border-b border-[#2A2A2A] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setDrawerOpen(true)} className="text-[#F5F5DC] p-2 -ml-2" aria-label="Open menu" data-testid="admin-menu-btn">
            <Menu size={22} />
          </button>
          <p className="font-display text-xl text-[#F5F5DC]">{currentLabel}</p>
          <button onClick={handleViewSite} className="text-[#A3A3A3] p-2 -mr-2" aria-label="View site">
            <Home size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-10">
          {maintenance && (
            <div
              data-testid="admin-maintenance-banner"
              className="mb-6 flex items-start gap-3 border-2 border-[#B87333] bg-[#B87333]/15 text-[#F5F5DC] px-4 py-3 sm:px-5 sm:py-4"
            >
              <AlertTriangle className="text-[#B87333] flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm sm:text-base leading-relaxed">
                <span className="font-bold uppercase tracking-widest text-[#B87333]">⚠ Maintenance Mode Active</span>
                <span className="ml-2">— Public visitors see the &ldquo;Workshop Closed&rdquo; page. Turn it off in <em>Settings</em> when you&apos;re ready to reopen.</span>
              </div>
            </div>
          )}
          <p className="text-[#A3A3A3] text-xs sm:text-sm mb-4 lg:mb-2 truncate" data-testid="admin-user">
            Signed in as {admin?.email}
          </p>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
